// src/lib/iqStats.js
//
// Shared statistics for IQ score conversion. Pure functions with no React or
// DOM dependency, so the same code runs on the server (for the pre-rendered
// reference tables and JSON-LD) and in the browser (for the live calculator).
//
// WHY THIS FILE EXISTS: the percentile maths was previously duplicated in
// src/app/page.js and src/app/articles/iq-scores/page.js. Both used the
// Abramowitz & Stegun 7.1.26 erf approximation, which is fine for a bell curve
// drawn at 100 points but is bounded by ABSOLUTE error of 1.5e-7. Deep in the
// tail that is useless: at IQ 175 the true upper tail is about 2.9e-7, so an
// absolute error of 1.5e-7 is over half the answer. A rarity figure computed
// that way would be wrong by a factor of two.
//
// The complementary error function below is a 24-term Chebyshev fit whose
// error is RELATIVE, not absolute, and sits near machine precision (verified
// to ~1e-15 against known values from z = 0 out to z = 6.7). Relative error is
// the property that matters here: it stays accurate however far into the tail
// you go. That is what lets the calculator quote "1 in 3.5 million" for IQ 175
// and be right.

// Complementary error function, erfc(x) = 1 - erf(x).
// Chebyshev expansion after Press et al., Numerical Recipes 3rd ed., 6.2.2.
function erfc(x) {
  const z = Math.abs(x)
  const t = 2.0 / (2.0 + z)
  const ty = 4.0 * t - 2.0

  const cof = [
    -1.3026537197817094, 6.4196979235649026e-1, 1.9476473204185836e-2,
    -9.561514786808631e-3, -9.46595344482036e-4, 3.66839497852761e-4,
    4.2523324806907e-5, -2.0278578112534e-5, -1.624290004647e-6,
    1.303655835580e-6, 1.5626441722e-8, -8.5238095915e-8,
    6.529054439e-9, 5.059343495e-9, -9.91364156e-10,
    -2.27365122e-10, 9.6467911e-11, 2.394038e-12,
    -6.886027e-12, 8.94487e-13, 3.13092e-13,
    -1.12708e-13, 3.81e-16, 7.106e-15,
  ]

  let d = 0.0
  let dd = 0.0
  for (let j = cof.length - 1; j > 0; j--) {
    const tmp = d
    d = ty * d - dd + cof[j]
    dd = tmp
  }

  const ans = t * Math.exp(-z * z + 0.5 * (cof[0] + ty * d) - dd)
  return x >= 0.0 ? ans : 2.0 - ans
}

// Standard normal CDF. Expressed through erfc rather than erf so that the
// small-probability branch is computed directly instead of as 1 minus a
// number close to 1, which would lose every significant digit in the tail.
export function normalCDF(z) {
  return 0.5 * erfc(-z / Math.SQRT2)
}

// Upper tail: the proportion of the population scoring ABOVE this z.
// Kept separate from 1 - normalCDF(z) for the same precision reason.
export function normalTailAbove(z) {
  return 0.5 * erfc(z / Math.SQRT2)
}

// Inverse standard normal CDF (the probit function).
// Acklam's algorithm, refined by one step of Halley's method against our own
// high-precision CDF above. The refinement takes the relative error from about
// 1.15e-9 to full double precision, which matters because the reverse mode of
// the calculator feeds this straight back into normalCDF to display a
// percentile - without the refinement, asking for the 99th percentile could
// return a score that reads back as 98.9999.
export function normalInverseCDF(p) {
  if (p <= 0) return -Infinity
  if (p >= 1) return Infinity

  const a = [
    -3.969683028665376e1, 2.209460984245205e2, -2.759285104469687e2,
    1.38357751867269e2, -3.066479806614716e1, 2.506628277459239,
  ]
  const b = [
    -5.447609879822406e1, 1.615858368580409e2, -1.556989798598866e2,
    6.680131188771972e1, -1.328068155288572e1,
  ]
  const c = [
    -7.784894002430293e-3, -3.223964580411365e-1, -2.400758277161838,
    -2.549732539343734, 4.374664141464968, 2.938163982698783,
  ]
  const d = [
    7.784695709041462e-3, 3.224671290700398e-1, 2.445134137142996,
    3.754408661907416,
  ]

  const pLow = 0.02425
  const pHigh = 1 - pLow
  let x

  if (p < pLow) {
    const q = Math.sqrt(-2 * Math.log(p))
    x =
      (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
      ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
  } else if (p <= pHigh) {
    const q = p - 0.5
    const r = q * q
    x =
      ((((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q) /
      (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1)
  } else {
    const q = Math.sqrt(-2 * Math.log(1 - p))
    x =
      -(((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
      ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
  }

  // One Halley step. e is the CDF error at our estimate, u the Newton step.
  const e = normalCDF(x) - p
  const u = e * Math.sqrt(2 * Math.PI) * Math.exp((x * x) / 2)
  x = x - u / (1 + (x * u) / 2)

  return x
}

// ---------------------------------------------------------------------------
// IQ-specific layer
// ---------------------------------------------------------------------------

// The three deviation scales still in circulation.
//
// Almost every modern test uses SD 15. The other two matter because Mensa
// publishes its entry requirement on all three, which is the single biggest
// source of confusion in IQ score reporting: 130, 132 and 148 are the SAME
// score, the 98th percentile, expressed on three different scales. People see
// the 132 figure, compare it against a WAIS result, and conclude they are two
// points short when they are not.
export const SCALES = [
  {
    id: 'sd15',
    sd: 15,
    label: 'SD 15',
    name: 'Wechsler scale',
    note: 'WAIS, WISC, Stanford-Binet 5, and effectively every modern test. If you are not sure which scale your score is on, it is almost certainly this one.',
    mensaEntry: 130,
  },
  {
    id: 'sd16',
    sd: 16,
    label: 'SD 16',
    name: 'Stanford-Binet L-M scale',
    note: 'The older Stanford-Binet Form L-M and the Binet-derived tests built on it. This is where the widely quoted Mensa figure of 132 comes from.',
    mensaEntry: 132,
  },
  {
    id: 'sd24',
    sd: 24,
    label: 'SD 24',
    name: 'Cattell scale',
    note: 'Cattell Culture Fair III and the Cattell III B, still used by Mensa in the UK. Its wide standard deviation is why Cattell scores look dramatically higher than they are.',
    mensaEntry: 148,
  },
]

export const DEFAULT_SCALE = SCALES[0]

export function getScale(id) {
  return SCALES.find((s) => s.id === id) || DEFAULT_SCALE
}

// Reliability coefficient used for the confidence interval.
//
// 0.98 is the internal-consistency reliability reported for WAIS-IV Full Scale
// IQ in the technical manual. On SD 15 that gives SEM = 15 * sqrt(0.02) = 2.12
// and a 95% interval of roughly plus or minus 4 points, which is why clinical
// reports quote a band rather than a single number. An online test is less
// reliable than a proctored clinical one, so treat this as the best case.
export const FSIQ_RELIABILITY = 0.98

export function standardErrorOfMeasurement(sd = 15) {
  return sd * Math.sqrt(1 - FSIQ_RELIABILITY)
}

// Range the calculator accepts. Beyond about 4.5 SD the normal model stops
// describing real test data at all - no test is normed on enough people to
// place a score that rare - so the UI flags scores outside this band rather
// than pretending the number means something.
export const MIN_IQ = 40
export const MAX_IQ = 200
export const MODEL_RELIABLE_MAX = 160

export function zScoreForIQ(iq, sd = 15) {
  return (iq - 100) / sd
}

export function iqForZScore(z, sd = 15) {
  return 100 + z * sd
}

// Percentile rank: the percentage of the population scoring at or below this.
export function percentileForIQ(iq, sd = 15) {
  return normalCDF(zScoreForIQ(iq, sd)) * 100
}

export function iqForPercentile(percentile, sd = 15) {
  return iqForZScore(normalInverseCDF(percentile / 100), sd)
}

// Converts a score from one deviation scale to another by holding z constant.
export function convertBetweenScales(iq, fromSd, toSd) {
  return iqForZScore(zScoreForIQ(iq, fromSd), toSd)
}

// How many people you would need before you'd expect to find one scoring this
// far from the mean, in the direction the score points.
//
// Returns the rarer tail: above the score for above-average scores, below it
// for below-average ones. A score of exactly 100 returns 2, which is correct
// and also the reason the UI does not lead with this number at the centre.
export function rarityOneIn(iq, sd = 15) {
  const z = zScoreForIQ(iq, sd)
  const tail = z >= 0 ? normalTailAbove(z) : normalCDF(z)
  if (tail <= 0) return Infinity
  return 1 / tail
}

// Formats a "1 in N" figure at a precision the underlying model can support.
//
// Rounding matters more than it looks. Quoting "1 in 31,560" for IQ 160 implies
// the norming sample could resolve one part in thirty thousand, which no test
// can. These thresholds keep the displayed precision roughly in line with what
// the number actually justifies.
export function formatOneIn(n) {
  if (!isFinite(n)) return 'more than 1 in a trillion'
  if (n < 10) return `1 in ${n.toFixed(1)}`
  if (n < 1000) return `1 in ${Math.round(n).toLocaleString('en-GB')}`
  if (n < 1e6) {
    const rounded = Math.round(n / 100) * 100
    return `1 in ${rounded.toLocaleString('en-GB')}`
  }
  if (n < 1e9) return `1 in ${(n / 1e6).toPrecision(2)} million`
  if (n < 1e12) return `1 in ${(n / 1e9).toPrecision(2)} billion`
  return `1 in ${(n / 1e12).toPrecision(2)} trillion`
}

// Percentiles are displayed at a precision that reflects how much of the
// distribution is actually being resolved. In the middle of the curve a whole
// number is plenty; in the tails the interesting digits are all after the
// decimal point, and rounding IQ 145 to "100th percentile" would be absurd.
export function formatPercentile(pct) {
  if (pct >= 99.999) return '>99.999'
  if (pct <= 0.001) return '<0.001'
  if (pct >= 99.9 || pct <= 0.1) return pct.toPrecision(5).replace(/0+$/, '').replace(/\.$/, '')
  if (pct >= 99 || pct <= 1) return pct.toFixed(2)
  if (pct >= 90 || pct <= 10) return pct.toFixed(1)
  return Math.round(pct).toString()
}

// Wechsler classification bands, matching the labels used in the IQ Score
// Guide and in clinical reports.
export const CLASSIFICATION_BANDS = [
  { min: 130, max: Infinity, label: 'Very Superior', blurb: 'Often called gifted. This is the Mensa entry threshold, the top 2 percent.', color: 'purple' },
  { min: 120, max: 129, label: 'Superior', blurb: 'Clearly high. Above the median for most professional and degree-level fields.', color: 'blue' },
  { min: 110, max: 119, label: 'High Average', blurb: 'Above average, and compatible with every professional career path.', color: 'emerald' },
  { min: 90, max: 109, label: 'Average', blurb: 'The statistical centre. Roughly half the population scores in this band.', color: 'slate' },
  { min: 80, max: 89, label: 'Low Average', blurb: 'Below average but within the normal range.', color: 'amber' },
  { min: 70, max: 79, label: 'Borderline', blurb: 'Below the normal range. Clinically, may prompt assessment of adaptive functioning.', color: 'orange' },
  { min: -Infinity, max: 69, label: 'Extremely Low', blurb: 'With adaptive functioning deficits, may indicate intellectual disability under DSM-5.', color: 'rose' },
]

// Classification is defined on the SD 15 scale, so a score on any other scale
// is converted before it is looked up. Without this, an SD 24 score of 130
// would be labelled "Very Superior" when it is really z = 1.25, or 111 on the
// Wechsler scale, which is High Average.
export function classifyIQ(iq, sd = 15) {
  const wechsler = sd === 15 ? iq : convertBetweenScales(iq, sd, 15)
  return CLASSIFICATION_BANDS.find((b) => wechsler >= b.min && wechsler <= b.max) || CLASSIFICATION_BANDS[3]
}

// The 95% confidence interval around an observed score.
export function confidenceInterval(iq, sd = 15) {
  const margin = 1.959963984540054 * standardErrorOfMeasurement(sd)
  return { low: iq - margin, high: iq + margin, margin }
}

// "In a room of N people, about M would score higher than you."
// Picks the smallest reference group that still yields a meaningful count, so
// the sentence never degrades into "about 0 people in a room of 100".
export function peopleWhoScoreHigher(iq, sd = 15) {
  const tail = normalTailAbove(zScoreForIQ(iq, sd))
  const groups = [
    { n: 100, label: 'a room of 100 people' },
    { n: 1000, label: 'a crowd of 1,000' },
    { n: 50000, label: 'a full football stadium of 50,000' },
    { n: 1000000, label: 'a city of a million' },
    { n: 68000000, label: 'the population of the UK' },
    { n: 8200000000, label: 'the entire world population' },
  ]
  for (const g of groups) {
    const count = tail * g.n
    if (count >= 1) return { ...g, count: Math.round(count) }
  }
  const last = groups[groups.length - 1]
  return { ...last, count: tail * last.n, fewerThanOne: true }
}
